import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  Badge,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import * as moment from "moment";
import Breadcrumbs from "components/Common/Breadcrumb";
import { sanitizeHtml, truncate } from "helpers/contentUtils";
import { toastSuccess, toastError } from "helpers/toast";
import { MODERATION_TYPES, alanEtiketi, alanDegeri } from "./moderationTypes";

import SayfaRehberi from "../../../components/Common/SayfaRehberi";
/**
 * Onay Bekleyenler — yoneticinin tum onay bekleyen icerikleri tek ekrandan
 * onaylayip kaldirabildigi sayfa.
 *
 * Veri dogrudan OData'dan (ODATA_BASE) cekiliyor; :9093 express wrapper'a
 * bagimlilik yok.
 */
const Moderation = () => {
  document.title = "Onay Bekleyenler | BERLİN ESNAF ADMIN PANELI";

  const [activeKey, setActiveKey] = useState(MODERATION_TYPES[0].key);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  // Onizleme: baslik ve icerik HTML olabildigi icin tabloda duz metin,
  // burada gercek gorunumuyle gosteriliyor.
  const [previewTarget, setPreviewTarget] = useState(null);

  const activeType =
    MODERATION_TYPES.find((t) => t.key === activeKey) || MODERATION_TYPES[0];

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const next = {};
    // Turler bagimsiz: biri patlarsa digerleri yine yuklensin
    await Promise.all(
      MODERATION_TYPES.map(async (type) => {
        try {
          const rsp = await type.list();
          next[type.key] = rsp.value || [];
        } catch (e) {
          console.warn("Onay listesi alinamadi:", type.key, e);
          next[type.key] = [];
          setError(
            "Bazı listeler yüklenemedi. Bağlantınızı kontrol edip sayfayı yenileyin."
          );
        }
      })
    );
    setItems(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const dropItem = (typeKey, id) => {
    setItems((prev) => ({
      ...prev,
      [typeKey]: (prev[typeKey] || []).filter((x) => x.ID !== id),
    }));
  };

  const handleApprove = async (item) => {
    setBusyId(item.ID);
    try {
      await activeType.approve(item.ID);
      dropItem(activeType.key, item.ID);
      setPreviewTarget(null);
      toastSuccess(`${activeType.singular} onaylandı.`);
    } catch (e) {
      setError(`${activeType.singular} onaylanamadı: ${e.message}`);
      toastError(`${activeType.singular} onaylanamadı.`);
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async () => {
    const item = confirmTarget;
    if (!item) return;
    setConfirmTarget(null);
    setBusyId(item.ID);
    try {
      await activeType.remove(item.ID);
      dropItem(activeType.key, item.ID);
      setPreviewTarget(null);
      toastSuccess(`${activeType.singular} silindi.`);
    } catch (e) {
      setError(`${activeType.singular} kaldırılamadı: ${e.message}`);
      toastError(`${activeType.singular} silinemedi.`);
    } finally {
      setBusyId(null);
    }
  };

  const totalPending = MODERATION_TYPES.reduce(
    (sum, t) => sum + ((items[t.key] || []).length),
    0
  );

  const rows = items[activeType.key] || [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Onay Bekleyenler" />
          <SayfaRehberi sayfa="moderation" />

          {error && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h5 className="mb-0">
                      Toplam onay bekleyen:{" "}
                      <Badge color="danger" className="font-size-12">
                        {totalPending}
                      </Badge>
                    </h5>
                    <Button color="light" onClick={loadAll} disabled={loading}>
                      <i className="bx bx-refresh font-size-16 align-middle me-1" />
                      Yenile
                    </Button>
                  </div>

                  <Nav tabs className="nav-tabs-custom mb-3 flex-wrap">
                    {MODERATION_TYPES.map((type) => {
                      const count = (items[type.key] || []).length;
                      return (
                        <NavItem key={type.key}>
                          <NavLink
                            style={{ cursor: "pointer" }}
                            className={activeKey === type.key ? "active" : ""}
                            onClick={() => setActiveKey(type.key)}
                          >
                            <i className={`${type.icon} align-middle me-1`} />
                            {type.label}{" "}
                            {count > 0 && (
                              <Badge color={type.color} className="ms-1">
                                {count}
                              </Badge>
                            )}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="text-muted mt-3 mb-0">
                        Onay bekleyenler yükleniyor...
                      </p>
                    </div>
                  ) : rows.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bx bx-check-circle text-success display-4" />
                      <h5 className="mt-3">Onay bekleyen yok</h5>
                      <p className="text-muted mb-0">
                        {activeType.label} için bekleyen içerik bulunmuyor.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "24%" }}>Başlık</th>
                            <th>İçerik</th>
                            <th style={{ width: "14%" }}>Gönderen</th>
                            <th style={{ width: "11%" }}>Tarih</th>
                            <th style={{ width: "24%" }} className="text-end">
                              İşlemler
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((item) => {
                            const busy = busyId === item.ID;
                            const author =
                              (item.Users &&
                                (item.Users.Users_Name_Surname ||
                                  item.Users.Users_Nickname)) ||
                              "Bilinmiyor";
                            const when = activeType.date(item);
                            const extra = activeType.extra(item);
                            return (
                              <tr key={item.ID}>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start fw-bold text-decoration-none"
                                    onClick={() => setPreviewTarget(item)}
                                    title="Önizle"
                                  >
                                    {activeType.title(item)}
                                  </button>
                                  {extra ? (
                                    <div className="text-muted font-size-12">
                                      {truncate(extra, 60)}
                                    </div>
                                  ) : null}
                                </td>
                                <td
                                  className="text-muted"
                                  style={{
                                    maxWidth: 380,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {truncate(activeType.body(item), 160) || (
                                    <em>İçerik yok</em>
                                  )}
                                </td>
                                <td>{author}</td>
                                <td>
                                  {when
                                    ? moment(when).format("DD.MM.YYYY HH:mm")
                                    : "-"}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => setPreviewTarget(item)}
                                  >
                                    <i className="bx bx-show align-middle me-1" />
                                    Önizle
                                  </Button>
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="me-2"
                                    disabled={busy}
                                    onClick={() => handleApprove(item)}
                                  >
                                    {busy ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <>
                                        <i className="bx bx-check align-middle me-1" />
                                        Onayla
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    outline
                                    disabled={busy}
                                    onClick={() => setConfirmTarget(item)}
                                  >
                                    <i className="bx bx-trash align-middle me-1" />
                                    Kaldır
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Onizleme: baslik ve icerik HTML gelebiliyor, burada gercek haliyle gosteriliyor */}
      <Modal
        isOpen={!!previewTarget}
        toggle={() => setPreviewTarget(null)}
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setPreviewTarget(null)}>
          {activeType.singular} Önizleme
        </ModalHeader>
        <ModalBody>
          {previewTarget && (
            <div>
              <h5
                className="mb-1"
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeHtml(
                      activeType.titleHtml
                        ? activeType.titleHtml(previewTarget)
                        : activeType.title(previewTarget)
                    ) || "<em>Başlık yok</em>",
                }}
              />

              <div className="text-muted font-size-12 mb-3">
                {(previewTarget.Users &&
                  (previewTarget.Users.Users_Name_Surname ||
                    previewTarget.Users.Users_Nickname)) ||
                  "Bilinmiyor"}
                {" · "}
                {activeType.date(previewTarget)
                  ? moment(activeType.date(previewTarget)).format(
                      "DD.MM.YYYY HH:mm"
                    )
                  : "Tarih yok"}
                {activeType.extra(previewTarget)
                  ? ` · ${activeType.extra(previewTarget)}`
                  : ""}
              </div>

              <div
                className="border rounded p-3 bg-white"
                style={{ wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeHtml(
                      activeType.bodyHtml
                        ? activeType.bodyHtml(previewTarget)
                        : activeType.body(previewTarget)
                    ) || "<em class='text-muted'>İçerik girilmemiş</em>",
                }}
              />

              {/* YORUMUN YAZILDIGI ICERIK
                  Yorum onaylanirken neye yazildigi da gorulmeli: baslik
                  yetmiyor, icerigin govdesi ve gorseli de karar icin gerekli
                  (ornegin uygunsuz bir konuya yazilmis masum bir yorum). */}
              {(() => {
                const hedef = activeType.hedef ? activeType.hedef(previewTarget) : null;
                if (!hedef) return null;
                return (
                  <div className="border rounded p-3 mt-3" style={{ background: "#f7f9fc" }}>
                    <div className="text-muted font-size-12 mb-1">
                      Bu yorum şuna yazıldı — {hedef.tur}
                    </div>
                    <div className="fw-bold mb-2">
                      {hedef.baslik || <em className="text-muted">Başlıksız</em>}
                    </div>
                    {hedef.govde ? (
                      <div
                        className="font-size-12 bg-white border rounded p-2"
                        style={{ maxHeight: 190, overflowY: "auto", wordBreak: "break-word" }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(hedef.govde) }}
                      />
                    ) : null}
                    {(hedef.gorseller || []).length > 0 ? (
                      <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
                        {hedef.gorseller.map((adres, i) => (
                          <a key={adres + i} href={adres} target="_blank" rel="noreferrer">
                            <img
                              src={adres}
                              alt=""
                              style={{
                                width: 110, height: 110, objectFit: "cover",
                                borderRadius: 8, border: "1px solid #e9edf2", background: "#fff",
                              }}
                              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                            />
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })()}

              {/* GORSELLER
                  Onay verirken icerigin fotograflari da gorulmeli; aksi halde
                  moderator neyi onayladigini bilmeden karar veriyordu.
                  Gorseli olmayan turlerde (yorumlar) `images` tanimli degil,
                  blok hic cizilmez. */}
              {(() => {
                const gorseller = activeType.images
                  ? activeType.images(previewTarget)
                  : [];
                if (gorseller.length === 0) return null;
                return (
                  <div className="mt-3">
                    <div className="text-muted font-size-12 mb-2">
                      Görseller ({gorseller.length})
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                      {gorseller.map((adres, i) => (
                        <a
                          key={adres + i}
                          href={adres}
                          target="_blank"
                          rel="noreferrer"
                          title="Büyütmek için tıklayın"
                        >
                          <img
                            src={adres}
                            alt=""
                            style={{
                              width: 150,
                              height: 150,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #e9edf2",
                              background: "#f7f9fc",
                            }}
                            // Dosya sunucudan silinmisse kirik ikon yerine
                            // kutucuk gizlenir.
                            onError={(e) => {
                              e.currentTarget.parentElement.style.display = "none";
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* TUM ALANLAR
                  Yukaridaki ozet moderasyon icin secilmis alanlari gosteriyor.
                  Karar vermek icin kayitin geri kalanina da bakmak gerekebilir
                  (adres, telefon, kategori, puan...). Katlanabilir tutuldu ki
                  ozet gorunum sade kalsin. */}
              <details className="mt-3">
                <summary
                  className="text-muted font-size-12"
                  style={{ cursor: "pointer" }}
                >
                  Tüm alanları göster
                </summary>
                <div className="table-responsive mt-2">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      {Object.entries(previewTarget)
                        .filter(([alan, deger]) => {
                          if (alan.startsWith("@")) return false;
                          if (deger === null || deger === "") return false;
                          // Nesne/dizi alanlar (Users, gorseller) zaten
                          // yukarida ozetleniyor.
                          if (typeof deger === "object") return false;
                          // Sifre gibi hassas alanlar asla gosterilmez.
                          return !/password|sifre/i.test(alan);
                        })
                        .map(([alan, deger]) => (
                          <tr key={alan}>
                            <td
                              className="text-muted"
                              style={{ width: 220, wordBreak: "break-word" }}
                            >
                              {alanEtiketi(alan)}
                            </td>
                            <td style={{ wordBreak: "break-word" }}>
                              {alanDegeri(alan, deger).slice(0, 500)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setPreviewTarget(null)}>
            Kapat
          </Button>
          <Button
            color="danger"
            outline
            onClick={() => {
              const item = previewTarget;
              setPreviewTarget(null);
              setConfirmTarget(item);
            }}
          >
            <i className="bx bx-trash align-middle me-1" />
            Kaldır
          </Button>
          <Button
            color="success"
            disabled={!previewTarget || busyId === (previewTarget || {}).ID}
            onClick={() => handleApprove(previewTarget)}
          >
            <i className="bx bx-check align-middle me-1" />
            Onayla
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!confirmTarget} toggle={() => setConfirmTarget(null)} centered>
        <ModalHeader toggle={() => setConfirmTarget(null)}>
          {activeType.singular} kaldırılsın mı?
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            <strong>
              {confirmTarget ? activeType.title(confirmTarget) : ""}
            </strong>
          </p>
          <p className="text-muted mb-0">
            Bu içerik kalıcı olarak silinecek. Bu işlem geri alınamaz.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmTarget(null)}>
            Vazgeç
          </Button>
          <Button color="danger" onClick={handleRemove}>
            Kaldır
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Moderation;
